<script setup lang="ts">
import { computed, inject } from 'vue'
import type { Container, Action, BackgroundImage } from '../types'
import { isSafeUrl, type UrlSanitizeOptions } from '@/lib/adaptive-card'
import { openSafeInNewTab } from '@/lib/urlSafety'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { useCardContext } from '../composables/useCardContext'
import { useValidation } from '../composables/useValidation'
import {
  spacingClasses,
  horizontalAlignClasses,
  verticalAlignClasses,
} from '../utils/classes'
import ACElement from './ACElement.vue'

const props = defineProps<{
  element: Container
  onAction?: (action: Action, inputs: Record<string, string>) => void
}>()

const { elements } = useCardContext()
const { validate, inputs } = useValidation(() => elements)
const urlOptions = inject<UrlSanitizeOptions>('ac-url-options', {})

// Only render as Card if container has visual styling
const isStyledContainer = computed(() => {
  return (props.element.style && props.element.style !== 'default') || props.element.backgroundImage
})

const containerStyleClasses: Record<string, string> = {
  default: '',
  emphasis: 'bg-muted',
  good: 'bg-green-50 dark:bg-green-900/20',
  attention: 'bg-red-50 dark:bg-red-900/20',
  warning: 'bg-orange-50 dark:bg-orange-900/20',
  accent: 'bg-blue-50 dark:bg-blue-900/20',
}

const backgroundStyle = computed(() => {
  if (!props.element.backgroundImage) return {}

  const bg = props.element.backgroundImage
  const url = typeof bg === 'string' ? bg : bg.url
  const fillMode = typeof bg === 'string' ? 'cover' : (bg as BackgroundImage).fillMode ?? 'cover'

  const style: Record<string, string> = {
    backgroundImage: `url(${url})`,
  }

  switch (fillMode) {
    case 'cover':
      style.backgroundSize = 'cover'
      style.backgroundPosition = 'center'
      style.backgroundRepeat = 'no-repeat'
      break
    case 'repeatHorizontally':
      style.backgroundRepeat = 'repeat-x'
      break
    case 'repeatVertically':
      style.backgroundRepeat = 'repeat-y'
      break
    case 'repeat':
      style.backgroundRepeat = 'repeat'
      break
  }

  if (typeof bg !== 'string') {
    const hAlign = (bg as BackgroundImage).horizontalAlignment
    const vAlign = (bg as BackgroundImage).verticalAlignment
    if (hAlign || vAlign) {
      style.backgroundPosition = `${hAlign ?? 'center'} ${vAlign ?? 'center'}`
    }
  }

  return style
})

const minHeightStyle = computed(() => {
  if (!props.element.minHeight) return {}
  const h = props.element.minHeight
  return {
    minHeight: h.endsWith('px') ? h : `${h}px`
  }
})

function handleClick() {
  if (!props.element.selectAction) return

  if (props.element.selectAction.type === 'Action.OpenUrl') {
    if (isSafeUrl(props.element.selectAction.url, urlOptions)) {
      openSafeInNewTab(props.element.selectAction.url)
    } else {
      console.warn('[AdaptiveCard] Blocked unsafe URL:', props.element.selectAction.url)
    }
    return
  }

  if (!props.onAction) return
  if (!validate()) return

  props.onAction(props.element.selectAction, { ...inputs })
}
</script>

<template>
  <!-- Styled container: use Card -->
  <Card
    v-if="isStyledContainer"
    :style="{ ...backgroundStyle, ...minHeightStyle }"
    :class="cn(
      'flex flex-col py-2 gap-0',
      spacingClasses[props.element.spacing ?? 'default'],
      props.element.horizontalAlignment && horizontalAlignClasses[props.element.horizontalAlignment],
      props.element.verticalContentAlignment && verticalAlignClasses[props.element.verticalContentAlignment],
      containerStyleClasses[props.element.style ?? 'default'],
      props.element.height === 'stretch' && 'flex-1',
      props.element.selectAction && 'cursor-pointer hover:bg-accent transition-colors'
    )"
    @click="handleClick"
  >
    <CardContent :class="cn('px-3 flex flex-col [&>*:first-child]:mt-0', props.element.height === 'stretch' && 'flex-1')">
      <ACElement
        v-for="(item, index) in props.element.items"
        :key="index"
        :element="item"
        :on-action="props.onAction"
      />
    </CardContent>
  </Card>

  <!-- Layout-only container: plain div -->
  <div
    v-else
    :style="minHeightStyle"
    :class="cn(
      'flex flex-col',
      spacingClasses[props.element.spacing ?? 'default'],
      props.element.horizontalAlignment && horizontalAlignClasses[props.element.horizontalAlignment],
      props.element.verticalContentAlignment && verticalAlignClasses[props.element.verticalContentAlignment],
      props.element.height === 'stretch' && 'flex-1',
      props.element.selectAction && 'cursor-pointer hover:bg-accent/50 transition-colors rounded'
    )"
    @click="handleClick"
  >
    <div class="flex flex-col [&>*:first-child]:mt-0">
      <ACElement
        v-for="(item, index) in props.element.items"
        :key="index"
        :element="item"
        :on-action="props.onAction"
      />
    </div>
  </div>
</template>
